import React from 'react';
import { getLogoSrc } from '../utils/logoHelper';
import { formatGameDate } from '../utils/dateFormatter';
import api from '../services/api';

const API_BASE_URL = api.defaults.baseURL.replace('/api', '');
const WIDTH = 1080;
const HEIGHT = 1080;

function GraphicRecapFinalScore({
  graphicData,
  teamSport,
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  awayTeamLogoPreview,
  showWatermark = true,
  dominantImage = null,
  heroFont = 'Bebas Neue',
  heroFontSize = null,
  accentBlockColor = null,
  detailTextColor = null,
  dominantImageX = null,
  dominantImageY = null,
  dominantImageScale = null,
  dominantImageOpacity = 100,
  dateFormat = 'full',
}) {
  const {
    homeTeam,
    homeScore,
    awayTeam,
    awayScore,
    gameDate,
    topPerformer,
    topPerformerStats,
  } = graphicData;

  const homeWon = parseInt(homeScore) > parseInt(awayScore);
  const isTie = parseInt(homeScore) === parseInt(awayScore);
  const awayWon = !homeWon && !isTie;
  const resultLabel = isTie ? 'TIE GAME' : homeWon ? 'VICTORY' : 'FINAL';

  const backgroundPhoto = graphicData.backgroundPhoto || null;
  const bgImageScale = (graphicData.bgImageScale ?? 100) / 100;
  const bgImageX = graphicData.bgImageX ?? 50;
  const bgImageY = graphicData.bgImageY ?? 50;
  const accent = accentBlockColor || teamColors.primary;
  const heroFontFamily = `'${heroFont}', 'Arial Narrow', Arial, sans-serif`;
  const barlowFamily = "'Barlow Condensed', 'Barlow', 'Arial Narrow', Arial, sans-serif";

  const getBackground = () => {
    switch (backgroundStyle) {
      case 'linear-gradient':
        return `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'radial-gradient':
        return `radial-gradient(circle at center, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'diagonal-gradient':
        return `linear-gradient(45deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'split-two-tone':
        return `linear-gradient(90deg, ${teamColors.primary} 50%, ${teamColors.secondary} 50%)`;
      case 'solid-primary':
        return teamColors.primary;
      case 'solid-secondary':
        return teamColors.secondary;
      default:
        return `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
    }
  };

  return (
    <div
      id="graphic-recap"
      style={{
        position: 'relative',
        width: WIDTH,
        height: HEIGHT,
        overflow: 'hidden',
        borderRadius: 16,
        background: getBackground(),
        fontFamily: heroFontFamily,
      }}
    >
      {/* Layer 1: Background photo (full bleed) */}
      {backgroundPhoto && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: WIDTH,
            height: HEIGHT,
            overflow: 'hidden',
            zIndex: 1,
          }}
        >
          <img
            src={backgroundPhoto}
            alt=""
            crossOrigin="anonymous"
            style={{
              position: 'absolute',
              top: `${bgImageY}%`,
              left: `${bgImageX}%`,
              transform: `translate(-50%, -50%) scale(${bgImageScale})`,
              transformOrigin: 'center center',
              width: 'auto',
              height: 'auto',
              minWidth: '100%',
              minHeight: '100%',
              maxWidth: 'none',
            }}
          />
        </div>
      )}

      {/* Layer 3: Result label banner */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: WIDTH,
          height: 160,
          background: accent,
          zIndex: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingTop: 20,
          paddingBottom: 20,
          paddingLeft: 80,
          paddingRight: 80,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          fontFamily: heroFontFamily,
          fontSize: heroFontSize ? Number(heroFontSize) : 100,
          color: '#ffffff',
          letterSpacing: 12,
        }}
      >
        {resultLabel}
      </div>

      {/* Layer 4 — Home logo */}
      {homeTeamLogo ? (
        <img
          src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
          alt={homeTeam || 'Home'}
          crossOrigin="anonymous"
          style={{
            position: 'absolute',
            top: 220,
            left: 270,
            transform: 'translateX(-50%)',
            height: 160,
            width: 'auto',
            zIndex: 4,
          }}
        />
      ) : (
        <div
          style={{
            position: 'absolute',
            top: 220,
            left: 0,
            width: 540,
            height: 160,
            zIndex: 4,
            fontSize: 120,
            textAlign: 'center',
            lineHeight: '160px',
          }}
        >
          {teamSport === 'Softball' ? '🥎' : '⚾'}
        </div>
      )}

      {/* Layer 4 — Home team name */}
      <div
        style={{
          position: 'absolute',
          top: 400,
          left: 0,
          width: 540,
          padding: '0 40px',
          zIndex: 4,
          fontFamily: heroFontFamily,
          fontSize: (homeTeam || '').length > 12 ? 38 : 52,
          color: '#ffffff',
          textAlign: 'center',
          letterSpacing: 3,
          lineHeight: 1.1,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {homeTeam || 'YOUR TEAM'}
      </div>

      {/* Layer 4 — Home score */}
      <div
        style={{
          position: 'absolute',
          top: 500,
          left: 0,
          width: 540,
          zIndex: 4,
          fontFamily: heroFontFamily,
          fontSize: 260,
          color: teamColors.secondary,
          textAlign: 'center',
          lineHeight: 1,
          textShadow: homeWon ? '0 0 60px rgba(255,255,255,0.3)' : 'none',
        }}
      >
        {homeScore || '0'}
      </div>

      {/* Layer 5 — Away logo */}
      {awayTeamLogoPreview ? (
        <img
          src={awayTeamLogoPreview}
          alt={awayTeam || 'Away'}
          crossOrigin="anonymous"
          style={{
            position: 'absolute',
            top: 220,
            left: 810,
            transform: 'translateX(-50%)',
            height: 160,
            width: 'auto',
            zIndex: 4,
          }}
        />
      ) : (
        <div
          style={{
            position: 'absolute',
            top: 220,
            left: 540,
            width: 540,
            height: 160,
            zIndex: 4,
            fontSize: 120,
            textAlign: 'center',
            lineHeight: '160px',
          }}
        >
          {teamSport === 'Softball' ? '🥎' : '⚾'}
        </div>
      )}

      {/* Layer 5 — Away team name */}
      <div
        style={{
          position: 'absolute',
          top: 400,
          left: 540,
          width: 540,
          padding: '0 40px',
          zIndex: 4,
          fontFamily: heroFontFamily,
          fontSize: (awayTeam || '').length > 12 ? 38 : 52,
          color: '#ffffff',
          textAlign: 'center',
          letterSpacing: 3,
          lineHeight: 1.1,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {awayTeam || 'OPPONENT'}
      </div>

      {/* Layer 5 — Away score */}
      <div
        style={{
          position: 'absolute',
          top: 500,
          left: 540,
          width: 540,
          zIndex: 4,
          fontFamily: heroFontFamily,
          fontSize: 260,
          color: awayWon ? '#ffffff' : 'rgba(255,255,255,0.6)',
          textAlign: 'center',
          lineHeight: 1,
          textShadow: awayWon ? '0 0 60px rgba(255,255,255,0.3)' : 'none',
        }}
      >
        {awayScore || '0'}
      </div>

      {/* Layer 6: Center vertical divider */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 538,
          width: 4,
          height: HEIGHT,
          background: accent,
          opacity: 0.4,
          zIndex: 5,
        }}
      />

      {/* Layer 7: Dash separator */}
      <div
        style={{
          position: 'absolute',
          top: 560,
          left: 540,
          transform: 'translateX(-50%)',
          zIndex: 6,
          fontFamily: heroFontFamily,
          fontSize: 60,
          color: 'rgba(255,255,255,0.5)',
          lineHeight: 1,
        }}
      >
        -
      </div>

      {/* Layer 7.5: Game date — always visible in middle zone */}
      {gameDate && (
        <div
          style={{
            position: 'absolute',
            top: 780,
            left: 0,
            width: WIDTH,
            zIndex: 5,
            textAlign: 'center',
            fontFamily: barlowFamily,
            fontSize: 36,
            color: 'rgba(255,255,255,0.6)',
            letterSpacing: 2,
            lineHeight: 1,
          }}
        >
          {formatGameDate(gameDate, dateFormat)}
        </div>
      )}

      {/* Layer 8: Bottom bar — topPerformer only */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: WIDTH,
          height: 200,
          background: accent,
          zIndex: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        {topPerformer ? (
          <>
            <div
              style={{
                fontFamily: barlowFamily,
                fontSize: 28,
                fontWeight: 700,
                color: '#ffffff',
                letterSpacing: 4,
                lineHeight: 1,
              }}
            >
              PLAYER OF THE GAME
            </div>
            <div
              style={{
                fontFamily: heroFontFamily,
                fontSize: 56,
                color: '#ffffff',
                lineHeight: 1,
              }}
            >
              {topPerformer}
            </div>
            {topPerformerStats && (
              <div
                style={{
                  fontFamily: barlowFamily,
                  fontSize: 28,
                  fontWeight: 600,
                  color: detailTextColor || 'rgba(255,255,255,0.85)',
                  lineHeight: 1.3,
                  textAlign: 'center',
                  padding: '0 40px',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                {topPerformerStats}
              </div>
            )}
          </>
        ) : null}
      </div>

      {/* Layer 9: Watermark */}
      {showWatermark && (
        <div
          style={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            zIndex: 9,
            background: 'rgba(255,255,255,0.92)',
            padding: '6px 12px',
            borderRadius: 8,
            pointerEvents: 'none',
          }}
        >
          <img src="/PMlogoleft.png" alt="Playmaker Media Kit" style={{ height: 80, width: 'auto', display: 'block' }} />
        </div>
      )}
    </div>
  );
}

export default GraphicRecapFinalScore;
